import { Flows } from "./constants";
import currency from "currency.js";
import { DateTime } from "luxon";
import { Enums } from "@shared";
import { Remarkable } from 'remarkable';

export const dayOrdinal = (d) => {

    if (!d) return null;
    return d + (d === 31 || d === 21 || d === 1 ? `st` : d === 22 || d === 2 ? `nd` : d === 23 || d === 3 ? `rd` : `th`);

};

export const printPercentage = (percentage) => {
    if (!percentage || percentage === ``) return ``;
    const fix = currency(percentage, { symbol: ``, precision: 4 }).multiply(100);
    return fix.cents() > 0 ? currency(fix, { symbol: ``, precision: 2 }).format() : currency(fix, { symbol: ``, precision: 0 }).format();
};

const findPath = (currentPath, steps) => {
    const path = currentPath.toLocaleLowerCase();
    const index = steps.findIndex(r => {
        const isArray = Array.isArray(r);
        const type = typeof r;
        return isArray && r.findIndex(p => p.indexOf(path) !== -1) !== -1 ||
            type === `string` && r.indexOf(path) !== -1 ||
            !isArray && type === `object` && r.path.indexOf(path) !== -1;
    });
    return index;
};

const basePath = (location, params) => {

    if (!params)
        return location.pathname;
    return Object.values(params).reduce(
        (path, param) => path.replace(`/${param}`, ``),
        location.pathname,
    );
};

const nextRoute = (location, state, overrides, params) => {
    let route = null;
    const { flow: flowName, start } = state;
    const flow = Flows[flowName];
    const path = basePath(location, params);
    let index = findPath(path, flow.steps);

    if (index === -1) throw new Error(`Next route not found. Flow: ${flow}`);

    while (index + 1 < flow.steps.length) {
        const nextStep = flow.steps[index + 1];
        if (nextStep.optional && nextStep.optional(overrides)) {
            index++;
        } else {
            route = nextStep.path || nextStep;
            break;
        }
    }
    if (route === `:start:`)
        return start;

    return route;
};

export const navigateStart = (location, navigate, flow, newOverrides = {}, newState = {}, params) => {

    const overrides = { ...newOverrides };
    const state = { ...newState, flow, overrides, start: location.pathname };
    const next = nextRoute(location, state, overrides, params);
    navigate(next, { state });
};

export const navigateTo = (to, location, navigate, newOverrides = {}, newState = {}) => {

    let overrides, state;
    if (location.state === null) {
        overrides = { ...newOverrides };
        state = { ...newState };
    } else {
        const { overrides: oldOverrides, ...rest } = location.state;
        overrides = { ...oldOverrides, ...newOverrides };
        state = { ...rest, ...newState };
    }

    if (!state.flow) {
        console.error(`Missing flow definition`);
    }

    navigate(to, { state: { ...state, overrides: { ...overrides } } });
};

export const navigateNext = (location, navigate, newOverrides = {}, newState = {}, params) => {

    let overrides, state;
    if (location.state === null) {
        overrides = { ...newOverrides };
        state = { ...newState };
    } else {
        const { overrides: oldOverrides, ...rest } = location.state;
        overrides = { ...oldOverrides, ...newOverrides };
        state = { ...rest, ...newState };
    }

    if (!state.flow) console.error(`Missing flow definition`);
    const finalState = { ...state, overrides: { ...overrides } };
    const next = nextRoute(location, finalState, overrides, params);
    navigate(next, { state: finalState });
};

export const compoundDay = (date, day) => DateTime.fromISO(date).startOf(`month`).set({ day: day });

export const amount = (percentage, total) => currency(percentage, { precision: 4 }).divide(100).multiply(total).value;

export const percentage = (amount, total) => currency(amount, { precision: 5 }).divide(total).value;

export const monthName = (date) => DateTime.fromISO(date).toLocaleString({ month: `long` });

export const parseMySQLDate = (rawDate) => new Date(rawDate.replace(`-`, `/`));

export const possessive = (string) => {

    if (string === ``) return string;

    const lastChar = string.slice(-1);
    const endOfWord = lastChar.toLowerCase() === `s` ? `'` : `'s`;
    return `${string}${endOfWord}`;
};

export const printFeeRule = (rule) => {

    if (rule.idFeeType === Enums.FeeType.Fixed)
        return currency(rule.fee).format();

    const feeP = currency(rule.fee, { precision: 3 }).multiply(100);
    const result = feeP.cents() > 0 ? currency(feeP, { precision: 1, symbol: `` }).format() : currency(feeP, { precision: 0, symbol: `` }).format();
    return `${result}%`;
};

export const nullPrint = (value) => value ?? ``;

export const toFixedDown = (value, precision) => {
    const re = new RegExp(`^-?\\d+(?:\\.\\d{0,${precision}})?`);
    const m = value.toString().match(re);
    return m ? currency(m[0], { precision }).value : value;
};

export const renderMarkdownToHTML = (markdown) => {
    const md = new Remarkable({ html: true });
    const renderedHTML = md.render(markdown);
    return { __html: renderedHTML };
};

export const sessionData = (session, selectedAccount) => {

    if (!session) return {};
    const { accounts, ...rest } = session;
    const account = accounts.find(a => a.idAccount === selectedAccount);
    return { ...rest, ...account };
};

export const showAccountSelectorInFlow = (location) => {

    const { state } = location || {};
    if (location !== null && state) {
        const { flow = null } = state;
        return !(flow && Flows[flow].accountSelector === false);
    }
    return true;
};

export const getDaysFromPayor = (orgDays, payorAutodrawDay, idProductType = null, potDeadline = null) => {
    const { autodrawDay: orgAutodrawDay, dueDay: orgDueDay } = orgDays;

    const locked = orgDueDay === orgAutodrawDay;
    let maxDay, autodrawDay;

    if (idProductType === Enums.ProductType.SunboundPay) {
        autodrawDay = payorAutodrawDay ?? potDeadline;
        maxDay = potDeadline;
    } else {
        autodrawDay = payorAutodrawDay ?? orgAutodrawDay;
        maxDay = autodrawDay > orgAutodrawDay ? autodrawDay : orgAutodrawDay;
    }
    return { maxDay, autodrawDay, locked };
};

export const isAutopayLate = (safeToday, day) => {
    const todayDay = DateTime.fromISO(safeToday);
    return parseInt(day) <= todayDay.day;
};

export const shouldResolveDefaults = (accounts, selectedAccount) => {
    // we could always try to resolve PM's even if the current account is fine
    // but for now we will skip resolution if the current account is fine
    const accountsNeedingResolution = accounts.filter(a =>
        !a.paymentPreferences.defaultPaymentMethod || a.paymentPreferences.defaultPaymentMethod?.disabledAt
    );
    const selectedAccountNeedingResolution = accountsNeedingResolution.find(a => a.idAccount === selectedAccount.idAccount);
    return selectedAccountNeedingResolution && accountsNeedingResolution.length > 1;
};